import { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import DateTimePicker from 'react-datetime';
import moment from 'moment';
import _ from 'lodash';

import {
  SongpopLegacyPackNotifScheduleEntryInput,
  UserSpendTier,
  UserSubscriptionTier,
} from '../../../../../../__gqltypes__';
import { applyChangeIfDateValid } from '../../../../../../utils/datepicker';
import { APP_VERSION_OPERATORS, textToUserIds, userIdsToText } from '../utils';

// Same tiers, labels and ranges as the sale event matchers (SaleEvent/components/SaleEventData.tsx).
export const SPEND_TIER_OPTIONS = [
  { tier: UserSpendTier.KRAKEN, label: 'Kraken', range: 'USD > 1,000' },
  { tier: UserSpendTier.WHALE, label: 'Whale', range: 'USD 100 - 1,000' },
  { tier: UserSpendTier.DOLPHIN, label: 'Dolphin', range: 'USD 20 - 100' },
  { tier: UserSpendTier.MINNOW, label: 'Minnow', range: 'USD 0 - 20' },
  { tier: UserSpendTier.NPAU, label: 'NPAU', range: 'never paid' },
] as const;

const ALL_TIERS = 'All';

// The matchers of one push: the same fields as the drop's, on the schedule entry.
export const PUSH_FILTER_KEYS = [
  'filterUserIds',
  'filterSubscriptionTier',
  'filterUserMinCreationDate',
  'filterUserMaxCreationDate',
  'filterAppVersion',
  'filterAppVersionOperator',
  'filterUserLevel',
  'filterExcludedUserIds',
  'filterHadSub',
  'filterSpendTiers',
] as const;
export type PushFilterFields = Pick<SongpopLegacyPackNotifScheduleEntryInput, typeof PUSH_FILTER_KEYS[number]>;

/** The matchers set on an entry, empty ones left out (so "none" compares equal however it is written). */
export function pushFiltersOf(entry: Partial<PushFilterFields>): PushFilterFields {
  const filters = _.omitBy(_.pick(entry, PUSH_FILTER_KEYS), (value) =>
    _.isArray(value) ? value.length === 0 : _.isNil(value) || value === false || value === ''
  ) as PushFilterFields;
  // every spend tier ticked = no spend filter (same as the drop)
  if ((filters.filterSpendTiers?.length ?? 0) >= SPEND_TIER_OPTIONS.length) {
    delete filters.filterSpendTiers;
  }
  // the operator only means something with a version
  if (!filters.filterAppVersion) {
    delete filters.filterAppVersionOperator;
  }
  // dates as Date, whatever the server sent (string or timestamp), so a stored push compares equal
  if (filters.filterUserMinCreationDate) {
    filters.filterUserMinCreationDate = new Date(filters.filterUserMinCreationDate);
  }
  if (filters.filterUserMaxCreationDate) {
    filters.filterUserMaxCreationDate = new Date(filters.filterUserMaxCreationDate);
  }
  return filters;
}

/** One line for the push row: who this push goes to. */
export function pushAudienceSummary(entry: Partial<PushFilterFields>): string {
  const filters = pushFiltersOf(entry);
  const date = (value: unknown) => moment(value as Date).format('YYYY-MM-DD');
  const parts = [
    filters.filterUserLevel != null && `Level ≥ ${filters.filterUserLevel}`,
    filters.filterSpendTiers &&
      filters.filterSpendTiers
        .map((tier) => SPEND_TIER_OPTIONS.find((option) => option.tier === tier)?.label ?? tier)
        .join(' / '),
    filters.filterSubscriptionTier && `Tier ${filters.filterSubscriptionTier}`,
    filters.filterHadSub && 'Never subscribed',
    filters.filterAppVersion && `App ${filters.filterAppVersionOperator ?? '>='} ${filters.filterAppVersion}`,
    filters.filterUserMinCreationDate && `Registered from ${date(filters.filterUserMinCreationDate)}`,
    filters.filterUserMaxCreationDate && `Registered until ${date(filters.filterUserMaxCreationDate)}`,
    filters.filterUserIds && `${filters.filterUserIds.length} user id(s)`,
    filters.filterExcludedUserIds && `${filters.filterExcludedUserIds.length} excluded`,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Everyone in the event';
}

type Props = {
  show: boolean;
  entry: Partial<PushFilterFields> | null;
  onHide: () => void;
  onApply: (filters: PushFilterFields) => void;
};

/**
 * Matchers of one push. They only narrow the event audience (the server checks the event matchers
 * first): a push never reaches a player who cannot see the offer.
 */
export function PushAudienceModal({ show, entry, onHide, onApply }: Props) {
  const [userIds, setUserIds] = useState('');
  const [excludedUserIds, setExcludedUserIds] = useState('');
  const [subscriptionTier, setSubscriptionTier] = useState<string>(ALL_TIERS);
  const [minCreation, setMinCreation] = useState<Date | undefined>();
  const [maxCreation, setMaxCreation] = useState<Date | undefined>();
  const [appVersion, setAppVersion] = useState('');
  const [appVersionOperator, setAppVersionOperator] = useState('>=');
  const [userLevel, setUserLevel] = useState('');
  const [hadSub, setHadSub] = useState(false);
  const [spendTiers, setSpendTiers] = useState<UserSpendTier[]>([]);

  // Opened on a push: start from its stored matchers.
  useEffect(() => {
    if (!show || !entry) {
      return;
    }
    setUserIds(userIdsToText(entry.filterUserIds));
    setExcludedUserIds(userIdsToText(entry.filterExcludedUserIds));
    setSubscriptionTier(entry.filterSubscriptionTier ?? ALL_TIERS);
    setMinCreation(entry.filterUserMinCreationDate ? new Date(entry.filterUserMinCreationDate) : undefined);
    setMaxCreation(entry.filterUserMaxCreationDate ? new Date(entry.filterUserMaxCreationDate) : undefined);
    setAppVersion(entry.filterAppVersion ?? '');
    setAppVersionOperator(entry.filterAppVersionOperator ?? '>=');
    setUserLevel(entry.filterUserLevel?.toString() ?? '');
    setHadSub(!!entry.filterHadSub);
    setSpendTiers(entry.filterSpendTiers ?? []);
  }, [show, entry]);

  const toggleSpendTier = (tier: UserSpendTier) =>
    setSpendTiers((old) => (old.includes(tier) ? old.filter((t) => t !== tier) : [...old, tier]));

  const apply = () => {
    const version = appVersion.trim();
    onApply(
      pushFiltersOf({
        filterUserIds: textToUserIds(userIds),
        filterExcludedUserIds: textToUserIds(excludedUserIds),
        filterSubscriptionTier: subscriptionTier === ALL_TIERS ? undefined : (subscriptionTier as UserSubscriptionTier),
        filterUserMinCreationDate: minCreation,
        filterUserMaxCreationDate: maxCreation,
        filterAppVersion: version || undefined,
        filterAppVersionOperator: version ? appVersionOperator : undefined,
        filterUserLevel: userLevel.trim() === '' ? undefined : Number(userLevel),
        filterHadSub: hadSub || undefined,
        filterSpendTiers: spendTiers,
      })
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Who receives this push</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">Leave everything empty to send to everyone who can see the event. Anything set here only removes players from the event audience, it never adds new ones.</p>
        <Row className="mb-3">
          <Col md={4}>Min user level:</Col>
          <Col md={3}>
            <Form.Control placeholder="any" value={userLevel} onChange={(e) => setUserLevel(e.target.value)} />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>Spend Tier (PAU status):</Col>
          <Col md={8}>
            <Form.Check
              type="radio"
              id="push-spend-tier-all"
              checked={spendTiers.length === 0}
              onChange={() => setSpendTiers([])}
              label={
                <>
                  All users <small className="text-muted">no spend filter</small>
                </>
              }
            />
            {SPEND_TIER_OPTIONS.map(({ tier, label, range }) => (
              <Form.Check
                key={tier}
                type="checkbox"
                id={`push-spend-tier-${tier}`}
                checked={spendTiers.includes(tier)}
                onChange={() => toggleSpendTier(tier)}
                label={
                  <>
                    {label} <small className="text-muted">{range}</small>
                  </>
                }
              />
            ))}
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>Subscription tier:</Col>
          <Col md={4}>
            <Form.Control as="select" value={subscriptionTier} onChange={(e) => setSubscriptionTier(e.target.value)}>
              <option value={ALL_TIERS}>All</option>
              {Object.values(UserSubscriptionTier).map((tier) => (
                <option key={tier} value={tier}>
                  {tier}
                </option>
              ))}
            </Form.Control>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>Never subscribed:</Col>
          <Col md={8}>
            <Form.Check
              type="checkbox"
              id="push-had-sub"
              checked={hadSub}
              onChange={(e) => setHadSub(e.target.checked)}
              label={<small className="text-muted">only users who never bought a subscription</small>}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>App version:</Col>
          <Col md={3}>
            <Form.Control
              as="select"
              value={appVersionOperator}
              onChange={(e) => setAppVersionOperator(e.target.value)}
            >
              {APP_VERSION_OPERATORS.map((operator) => (
                <option key={operator} value={operator}>
                  {operator}
                </option>
              ))}
            </Form.Control>
          </Col>
          <Col md={4}>
            <Form.Control placeholder="any" value={appVersion} onChange={(e) => setAppVersion(e.target.value)} />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>Minimum registration date:</Col>
          <Col md={6}>
            <DateTimePicker
              dateFormat="YYYY-MM-DD"
              timeFormat
              // @ts-ignore
              onChange={applyChangeIfDateValid(setMinCreation)}
              // @ts-ignore
              value={minCreation}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>Maximum registration date:</Col>
          <Col md={6}>
            <DateTimePicker
              dateFormat="YYYY-MM-DD"
              timeFormat
              // @ts-ignore
              onChange={applyChangeIfDateValid(setMaxCreation)}
              // @ts-ignore
              value={maxCreation}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>
            User ids:
            <br />
            <small className="text-muted">separated by commas</small>
          </Col>
          <Col md={8}>
            <Form.Control as="textarea" value={userIds} onChange={(e) => setUserIds(e.target.value)} />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            Excluded user ids:
            <br />
            <small className="text-muted">separated by commas</small>
          </Col>
          <Col md={8}>
            <Form.Control as="textarea" value={excludedUserIds} onChange={(e) => setExcludedUserIds(e.target.value)} />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={apply}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
